<template>
  <div class="custom-service">
    <NoData v-if="!deleteModeItems.length && deleteMode" />
    <template v-if="deleteModeItems.length">
      <div v-for="(item, index) in deleteModeItems" :key="index">
        <div class="group-name">{{ item.category.name }}</div>

        <GmbServicesMassTreeNode
          class="custom-service__tree-node"
          :services="item.service_items"
          :delete-mode="deleteMode"
          @input="handleDeleteInput"
        />
      </div>
    </template>
    <template v-if="!deleteMode">
      <div class="custom-service__info">
        <div class="info-icon">
          <v-icon size="17">
            mdi-information-outline
          </v-icon>
        </div>
        <span>
          {{ $t("message.facilityDetails.services.addServiceInfo") }}
        </span>
      </div>
      <vue-perfect-scrollbar
        ref="listWrapper"
        class="custom-service__list-wrapper"
      >
        <v-form
          ref="form"
          v-model="isFormValid"
          class="config-wrapper"
          @submit.prevent
        >
          <GmbServiceMassCustomServiceListItem
            v-for="service in customServicesList"
            :value="service"
            :name="`custom-service-${service.id}`"
            v-validate="'service_exists'"
            :error-messages="errors.collect(`custom-service-${service.id}`)"
            :key="service.id"
            :show-remove-button="showRemoveButton"
            @remove-custom-service="handleRemoveService(service.id)"
            @change="handleChange"
            @add-custom-service="handleAddServiceRow"
            @input="handleInput(service.id, $event)"
          />
        </v-form>
      </vue-perfect-scrollbar>
      <CustomServiceAddButton
        :label="$t('message.facilityDetails.services.addNext')"
        bottom
        class="custom-service__add-button"
        @click="handleAddServiceRow"
      />
    </template>
    <SectionLoader :status="isLoading" :opacity="1" />
  </div>
</template>

<script>
import cuid from "cuid";
import {
  SERVICE_ACTIONS_ENUM,
  SERVICE_ITEM_TYPE_ENUM
} from "@/enums/gmb-services.enum";
import GmbServiceCustomServiceAddButton from "./GmbServiceCustomServiceAddButton.vue";
import { Validator } from "vee-validate";
import GmbServices from "@/services/gmbServices";
import GmbServicesMassTreeNode from "@/components/FacilityDetails/GmbServices/GmbServicesMassTreeNode.vue";
import NoData from "@/components/ui/NoData";
import SectionLoader from "@/components/Loader/SectionLoader";

export default {
  name: "GmbServiceMassCustomService",
  components: {
    GmbServiceMassCustomServiceListItem: () =>
      import("./GmbServiceMassCustomServiceListItem.vue"),
    CustomServiceAddButton: GmbServiceCustomServiceAddButton,
    GmbServicesMassTreeNode,
    NoData,
    SectionLoader
  },
  props: {
    deleteMode: {
      type: Boolean,
      default: false
    },
    facilitiesIds: {
      type: Array,
      default: () => []
    },
    preselectedCustomServices: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      validationErrors: {},
      customServicesList: null,
      deleteModeItems: [],
      isFormValid: false,
      isLoading: false
    };
  },
  computed: {
    showRemoveButton() {
      return this.customServicesList.length > 1;
    },
    hasCustomServices() {
      return !!this.customServicesList.length;
    }
  },
  async created() {
    if (this.preselectedCustomServices.length) {
      const preselectedCustomServicesWithIds = this.preselectedCustomServices.map(
        service => {
          return {
            id: cuid(),
            name: service.display_name,
            ...(service.description && { description: service.description })
          };
        }
      );
      this.customServicesList = preselectedCustomServicesWithIds;
    } else {
      this.customServicesList = [{ id: cuid(), name: "", description: "" }];
    }
    if (this.deleteMode) {
      this.isLoading = true;
      await this.getGmbServicesFreeForm();
      this.isLoading = false;
    }
  },
  mounted() {
    Validator.extend("service_exists", {
      getMessage: (fieldId, fieldValue) => {
        return this.$t("message.facilityDetails.services.duplicateError", [
          fieldValue[0]
        ]);
      },
      validate: value => {
        return {
          valid: !this.checkIfServiceExists(value),
          data: value.name
        };
      }
    });
  },

  methods: {
    checkIfServiceExists(serviceName) {
      const serviceNameLower = serviceName.name.toLowerCase();

      return this.customServicesList.some(service => {
        return (
          this.isDisplayNameMatch(service, serviceNameLower) &&
          serviceName.id !== service.id
        );
      });
    },
    isDisplayNameMatch(service, serviceNameLower) {
      return service.name && service.name.toLowerCase() === serviceNameLower;
    },

    handleDeleteInput(deletedItems) {
      this.customServicesList = deletedItems;
      const data = {
        type: SERVICE_ACTIONS_ENUM.DELETE,
        service_items: deletedItems.map(service => {
          return {
            type: SERVICE_ITEM_TYPE_ENUM.FREE_FORM,
            display_name: service.display_name
          };
        })
      };
      this.$emit("change", data);
    },
    async getGmbServicesFreeForm() {
      const { data } = await GmbServices.getGmbServicesListFreeForm({
        facilities_ids: this.facilitiesIds.join(",")
      });
      this.deleteModeItems = data;
    },
    isDescriptionTooLong(description) {
      return description && description.length > 300;
    },
    ifDescriptionExistsWithoutName(description, name) {
      return description && !name;
    },

    updateService(serviceId, newService) {
      const serviceIndex = this.customServicesList.findIndex(
        service => service.id === serviceId
      );
      this.customServicesList[serviceIndex].name = newService.name;
      this.customServicesList[serviceIndex].description =
        newService.description;
    },

    handleInput(serviceId, newService) {
      this.updateService(serviceId, newService);
    },

    handleAddServiceRow() {
      this.customServicesList.push({ id: cuid(), name: "" });
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
    handleRemoveService(id) {
      this.customServicesList = this.customServicesList.filter(
        service => service.id !== id
      );
      this.handleChange();
    },
    clearServicesList() {
      this.customServicesList = [];
    },
    createServiceItems(customServices) {
      const filteredServices = customServices.filter(
        service => service.name.trim().length > 0
      );
      return {
        type: SERVICE_ACTIONS_ENUM.CREATE,
        service_items: filteredServices.map(service => {
          return {
            type: SERVICE_ITEM_TYPE_ENUM.FREE_FORM,
            display_name: service.name,
            description: service.description
          };
        })
      };
    },
    handleChange() {
      const updatedServicesList = this.createServiceItems(
        this.customServicesList
      );

      this.$emit("change", updatedServicesList);
    },
    scrollToBottom() {
      const ps = this.$refs.listWrapper.ps;
      const contentHeight = ps.element.scrollHeight;
      ps.element.scrollTop = contentHeight;
      ps.update();
    }
  },
  watch: {
    isFormValid(current) {
      this.$emit("validate", current);
    }
  }
};
</script>
<style lang="scss" scoped>
.config-wrapper {
  display: flex;
  flex-direction: column;
  gap: $spacing * 0.75;
}
.custom-service {
  &__tree-node {
    padding-top: 0;
  }
  &__info {
    @include font-size(12px);
    line-height: 17px;
    color: $grey-darker;
    margin-bottom: $spacing;
    display: flex;
    gap: $spacing * 0.5;

    .info-icon {
      opacity: 0.7;
    }
  }

  &__list-wrapper {
    max-height: 360px;
    padding-bottom: $spacing * 0.2;
  }

  &__add-button {
    margin-top: $spacing * 0.5;
    margin-left: $spacing * 1.25;
  }

  &__empty-state {
    @include font-size(12px);
    display: flex;
    align-items: center;
    gap: $spacing;

    span {
      color: $grey-darker;
    }
  }
}
.group-name {
  font-weight: 600;
  @include font-size(16px);
  line-height: 20px;
  color: $grey-darker;
  margin-bottom: $spacing * 1.25;
}
</style>
